import { useEffect, useState, type FormEvent } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import DashboardShell from './DashboardShell';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Booking, Offer, OfferStatus, Vendor } from '../../lib/database.types';
import { formatDate, formatMoney, formatTime } from '../../lib/format';

const tabs = [
  { label: 'Offers', href: '/dashboard' },
  { label: 'Bookings', href: '/dashboard/bookings' },
  { label: 'Profile', href: '/dashboard/profile' },
];

export default function VendorDashboard() {
  return (
    <DashboardShell role="vendor" tabs={tabs}>
      <Routes>
        <Route index element={<Offers />} />
        <Route path="offers/new" element={<OfferEdit mode="new" />} />
        <Route path="offers/:id" element={<OfferEdit mode="edit" />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardShell>
  );
}

function useVendor() {
  const { user } = useAuth();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: m } = await supabase.from('ga_vendor_members').select('vendor_id').eq('user_id', user.id).limit(1).maybeSingle();
      if (!m) {
        setLoading(false);
        return;
      }
      const { data: v } = await supabase.from('ga_vendors').select('*').eq('id', m.vendor_id).maybeSingle();
      setVendor(v ?? null);
      setLoading(false);
    })();
  }, [user]);

  return { vendor, setVendor, loading };
}

function Offers() {
  const { vendor, loading } = useVendor();
  const [offers, setOffers] = useState<Offer[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!vendor) return;
    (async () => {
      const { data } = await supabase.from('ga_offers').select('*').eq('vendor_id', vendor.id).order('created_at', { ascending: false });
      setOffers(data ?? []);
    })();
  }, [vendor]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!vendor) return <NoVendor />;

  return (
    <div>
      <SectionHeader
        eyebrow={vendor.name}
        title="Offers"
        action={<button onClick={() => navigate('/dashboard/offers/new')} className="btn btn-primary btn-sm">+ New offer</button>}
      />
      {offers.length === 0 ? (
        <EmptyState title="No offers yet" body="Create your first happy hour package. Teams will see it on your bar's page." />
      ) : (
        <div className="stack">
          {offers.map((o) => (
            <div key={o.id} style={{ padding: 24, borderRadius: 16, background: 'var(--card)', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 16, alignItems: 'center' }}>
              <div>
                <div className="display" style={{ fontSize: 22 }}>{o.title}</div>
                <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4 }}>
                  {formatMoney(o.price_cents)}/head · {o.window_days} · {formatTime(o.window_start)}–{formatTime(o.window_end)} · {o.headcount_min}–{o.headcount_max} ppl
                </div>
              </div>
              <span className={`tag ${o.status === 'active' ? 'tag-active' : 'tag-pending'}`}>{o.status}</span>
              <button onClick={() => navigate(`/dashboard/offers/${o.id}`)} className="btn btn-ghost btn-sm">Edit</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OfferEdit({ mode }: { mode: 'new' | 'edit' }) {
  const { id } = useParams();
  const { vendor, loading } = useVendor();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState(40);
  const [headcountMin, setHeadcountMin] = useState(6);
  const [headcountMax, setHeadcountMax] = useState(40);
  const [windowDays, setWindowDays] = useState('Tue-Thu');
  const [windowStart, setWindowStart] = useState('17:00');
  const [windowEnd, setWindowEnd] = useState('19:00');
  const [status, setStatus] = useState<OfferStatus>('draft');

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    (async () => {
      const { data } = await supabase.from('ga_offers').select('*').eq('id', id).maybeSingle();
      if (!data) return;
      setTitle(data.title);
      setDescription(data.description ?? '');
      setPriceDollars(data.price_cents / 100);
      setHeadcountMin(data.headcount_min);
      setHeadcountMax(data.headcount_max);
      setWindowDays(data.window_days);
      setWindowStart(data.window_start.slice(0, 5));
      setWindowEnd(data.window_end.slice(0, 5));
      setStatus(data.status);
    })();
  }, [id, mode]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!vendor) return <NoVendor />;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const payload = {
      vendor_id: vendor!.id,
      title,
      description: description || null,
      price_cents: Math.round(priceDollars * 100),
      headcount_min: headcountMin,
      headcount_max: headcountMax,
      window_days: windowDays,
      window_start: `${windowStart}:00`,
      window_end: `${windowEnd}:00`,
      status,
    };
    const res = mode === 'new'
      ? await supabase.from('ga_offers').insert(payload)
      : await supabase.from('ga_offers').update(payload).eq('id', id!);
    setSubmitting(false);
    if (res.error) {
      setError(res.error.message);
      return;
    }
    navigate('/dashboard');
  }

  async function onDelete() {
    if (mode !== 'edit' || !id) return;
    if (!confirm('Delete this offer? This cannot be undone.')) return;
    await supabase.from('ga_offers').delete().eq('id', id);
    navigate('/dashboard');
  }

  return (
    <div>
      <SectionHeader eyebrow={vendor.name} title={mode === 'new' ? 'New offer' : 'Edit offer'} />
      <form onSubmit={onSubmit} style={{ padding: 32, borderRadius: 18, background: 'var(--card)', maxWidth: 760 }}>
        <div className="stack">
          <label>
            <span className="field-label">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <label>
            <span className="field-label">Description</span>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <label>
              <span className="field-label">Price per head ($)</span>
              <input type="number" min={0} step={1} value={priceDollars} onChange={(e) => setPriceDollars(Number(e.target.value))} required />
            </label>
            <label>
              <span className="field-label">Min headcount</span>
              <input type="number" min={1} value={headcountMin} onChange={(e) => setHeadcountMin(Number(e.target.value))} required />
            </label>
            <label>
              <span className="field-label">Max headcount</span>
              <input type="number" min={1} value={headcountMax} onChange={(e) => setHeadcountMax(Number(e.target.value))} required />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <label>
              <span className="field-label">Days</span>
              <input value={windowDays} onChange={(e) => setWindowDays(e.target.value)} placeholder="Tue-Thu" required />
            </label>
            <label>
              <span className="field-label">Start</span>
              <input type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} required />
            </label>
            <label>
              <span className="field-label">End</span>
              <input type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} required />
            </label>
          </div>
          <label>
            <span className="field-label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as OfferStatus)}>
              <option value="draft">Draft (hidden)</option>
              <option value="active">Active (visible to teams)</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          {error && <div className="field-error">{error}</div>}
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : mode === 'new' ? 'Create offer' : 'Save changes'}
              </button>
              <button type="button" onClick={() => navigate('/dashboard')} className="btn btn-ghost">Cancel</button>
            </div>
            {mode === 'edit' && (
              <button type="button" onClick={onDelete} className="btn btn-danger btn-sm">Delete offer</button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

function Bookings() {
  const { vendor, loading } = useVendor();
  const [bookings, setBookings] = useState<(Booking & { offer: Offer | null })[]>([]);

  useEffect(() => {
    if (!vendor) return;
    (async () => {
      const { data: b } = await supabase.from('ga_bookings').select('*').eq('vendor_id', vendor.id).order('booking_date', { ascending: false });
      if (!b) return;
      const offerIds = [...new Set(b.map((x) => x.offer_id))];
      const { data: offers } = await supabase.from('ga_offers').select('*').in('id', offerIds);
      const map = new Map((offers ?? []).map((o) => [o.id, o]));
      setBookings(b.map((x) => ({ ...x, offer: map.get(x.offer_id) ?? null })));
    })();
  }, [vendor]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!vendor) return <NoVendor />;

  return (
    <div>
      <SectionHeader eyebrow={vendor.name} title="Bookings" />
      {bookings.length === 0 ? (
        <EmptyState title="No bookings yet" body="When teams reserve one of your offers, they'll appear here with headcount and contact info." />
      ) : (
        <div className="stack">
          {bookings.map((b) => (
            <div key={b.id} style={{ padding: 24, borderRadius: 16, background: 'var(--card)' }}>
              <div className="row-between">
                <div>
                  <div className="display" style={{ fontSize: 22 }}>{b.offer?.title || '-'}</div>
                  <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4 }}>
                    {formatDate(b.booking_date)} · {formatTime(b.booking_time)} · {b.headcount} people
                  </div>
                  {b.notes && <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 8, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8 }}><strong>Notes:</strong> {b.notes}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="display" style={{ fontSize: 22, color: 'var(--wine)' }}>{formatMoney(b.total_cents)}</div>
                  <span className={`tag ${b.status === 'confirmed' ? 'tag-active' : b.status === 'cancelled' ? 'tag-cancelled' : 'tag-pending'}`}>{b.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Profile() {
  const { vendor, setVendor, loading } = useVendor();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (loading) return <p className="muted">Loading…</p>;
  if (!vendor) return <NoVendor />;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!vendor) return;
    setError(null);
    setSaved(false);
    setSubmitting(true);
    const { error, data } = await supabase
      .from('ga_vendors')
      .update({
        name: vendor.name,
        neighborhood: vendor.neighborhood,
        address: vendor.address,
        description: vendor.description,
        contact_email: vendor.contact_email,
      })
      .eq('id', vendor.id)
      .select()
      .maybeSingle();
    setSubmitting(false);
    if (error) {
      setError(error.message);
      return;
    }
    if (data) setVendor(data);
    setSaved(true);
  }

  return (
    <div>
      <SectionHeader eyebrow={vendor.slug} title="Bar profile" />
      <form onSubmit={save} style={{ padding: 32, borderRadius: 18, background: 'var(--card)', maxWidth: 760 }}>
        <div className="stack">
          <label>
            <span className="field-label">Name</span>
            <input value={vendor.name} onChange={(e) => setVendor({ ...vendor, name: e.target.value })} required />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
            <label>
              <span className="field-label">Neighborhood</span>
              <input value={vendor.neighborhood ?? ''} onChange={(e) => setVendor({ ...vendor, neighborhood: e.target.value })} />
            </label>
            <label>
              <span className="field-label">Address</span>
              <input value={vendor.address ?? ''} onChange={(e) => setVendor({ ...vendor, address: e.target.value })} />
            </label>
          </div>
          <label>
            <span className="field-label">Description</span>
            <textarea rows={4} value={vendor.description ?? ''} onChange={(e) => setVendor({ ...vendor, description: e.target.value })} />
          </label>
          <label>
            <span className="field-label">Contact email</span>
            <input type="email" value={vendor.contact_email ?? ''} onChange={(e) => setVendor({ ...vendor, contact_email: e.target.value })} />
          </label>
          {error && <div className="field-error">{error}</div>}
          {saved && <div style={{ color: 'var(--success)' }}>Saved.</div>}
          <button className="btn btn-primary" disabled={submitting} style={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function NoVendor() {
  return (
    <EmptyState
      title="No bar linked to this account"
      body="Ask a platform admin to link your user to a vendor, or apply to list a new bar on the For Bars page."
    />
  );
}

function SectionHeader({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: 28, gap: 24, flexWrap: 'wrap' }}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 12 }}>{eyebrow}</div>
        <h1 className="display" style={{ fontSize: 'clamp(32px, 4vw, 48px)', margin: 0 }}>{title}</h1>
      </div>
      {action}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ padding: 40, borderRadius: 18, background: 'var(--card)', textAlign: 'center' }}>
      <div className="display" style={{ fontSize: 28, marginBottom: 8 }}>{title}</div>
      <p className="muted" style={{ maxWidth: 480, margin: '0 auto' }}>{body}</p>
    </div>
  );
}
